import { Gender } from '../../../common/enums/gender.enum'
import { LinkType } from '../../../common/enums/link-type.enum'
import { YieldType } from '../../../common/enums/yield-type.enum'
import { ResourceDefinition } from '../../../common/interfaces/resource-definition.interface'
import { Invoice } from '../../invoice/invoice.interface'
import { ProjectStatus } from '../project-status.enum'

export const projectAccountingInvoiceDefinition: ResourceDefinition = {
  title: 'Factures',
  nameSingular: 'facture',
  namePlural: 'fatures',
  gender: Gender.Feminine,
  slug: 'invoices',
  path: 'factures',
  hasDetailPage: false,
  hasListPage: true,
  buttons: [LinkType.CREATE, LinkType.EXPORT],
  defaultLink: null,
  childrenThatPreventDelete: [
    {
      propName: 'payments',
      preventDeleteMessage: `Certains paiements ont déjà été effectués pour cette facture`
    },
    {
      propName: 'credits',
      preventDeleteMessage: `Un ou plusieurs avoirs a été réalisé pour cette facture. Veuillez les supprimer d'abord`
    },
    {
      propName: 'expenseCount',
      preventDeleteMessage: `Une ou plusieurs dépenses sont reliées à cette facture`
    }
  ],
  actionButtons: [
    // ABAC instead of RBAC like invoiceDefinition.
    {
      label: 'Valider',
      className: 'button is-primary p-x-lg',
      condition: (i: Invoice) =>
        !i.validated &&
        (i.project.status === ProjectStatus.Signed ||
          i.project.status === ProjectStatus.Finished),
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      },
      patchAction: (i: Invoice) => ({
        resourceName: `projects/${i.project.id}/invoices`,
        id: i.id,
        suffix: 'validate',
        successMessage: `La facture ${i.number} a bien été validé.`,
        errorMessage: `Une erreur a eu lieu lors de la validation de la facture ${i.number}.`
      })
    },
    {
      label: 'Ajouter un paiement',
      className: 'button is-info p-x-lg',
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      },
      condition: (i: Invoice) => i.validated && i.paymentPercentage < 1,
      linkAction: (i: Invoice) => ({
        path: `/missions/${i.project.id}`,
        queryParams: {
          selectedTab: 'accounting',
          invoiceToPay: i.id,
          reload: new Date().toISOString()
        }
      })
    }
  ],
  dropdownLinks: [
    {
      type: LinkType.DUPLICATE,
      duplicatePath: (i: Invoice) => `projects/${i.project.id}/invoices`,
      condition: (i: Invoice) => !!i.project.customer,
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      }
    },
    {
      type: LinkType.CUSTOM,
      label: 'Modifier',
      condition: (i: Invoice) => !i.validated,
      linkAction: (i: Invoice) => ({
        path: `/missions/${i.project.id}/factures/${i.id}/edit`
      }),
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      }
    },
    {
      type: LinkType.CUSTOM,
      label: 'Ajouter un commentaire',
      linkAction: (i: Invoice) => ({
        path: `/missions/${i.project.id}`,
        queryParams: {
          selectedTab: 'accounting',
          invoiceToComment: i.id,
          reload: new Date().toISOString()
        }
      }),
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      }
    },
    {
      type: LinkType.DELETE,
      deletePath: (i: Invoice) => `projects/${i.project.id}/invoices`,
      condition: (i: Invoice) => !i.validated,
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      }
    },
    {
      type: LinkType.CUSTOM,
      label: 'Supprimer un paiement',
      condition: (i: Invoice) => !!i.paymentDates,
      linkAction: (i: Invoice) => ({
        path: `/missions/${i.project.id}`,
        queryParams: {
          selectedTab: 'accounting',
          invoiceToPay: i.id,
          reload: new Date().toISOString()
        }
      }),
      policy: {
        name: 'manageAccounting',
        resourceSlug: 'projects',
        resourceId: (i: Invoice) => i.project.id
      }
    }
  ],
  yields: [
    {
      label: 'Numéro',
      property: 'number'
    },
    {
      label: 'Libellé',
      property: 'reference'
    },
    {
      label: 'Mission ',
      property: 'project.label',
      link: (i) => `missions/${i.project.id}`
    },
    {
      label: 'Client',
      property: 'project.customer.name',
      orderByProperty: 'customer.name',
      link: (invoice: Invoice) =>
        invoice.project.customer
          ? `clients/${invoice.project.customer.id}`
          : null
    },
    {
      label: `Date d'émission`,
      property: 'issueDate',
      type: YieldType.Date
    },
    {
      label: `Date d'échéance`,
      property: 'dueDate',
      type: YieldType.Date
    },
    {
      label: 'Montant HT',
      property: 'amount',
      type: YieldType.Currency
    },
    {
      label: 'Montant TTC',
      property: 'amountWithTaxes',
      type: YieldType.Currency
    },
    {
      label: 'Budget',
      property: 'budget.name'
    },
    {
      label: 'Reglement',
      headingClassName: 'has-text-right',
      property: 'paymentPercentage',
      type: YieldType.AnalogProgressBar,
      disableOrderBy: true
    },
    {
      label: '',
      property: 'paymentDates'
    },
    {
      label: '',
      property: 'path',
      type: YieldType.Download
    },
    {
      label: '',
      property: 'comments',
      type: YieldType.Icon
    }
  ]
}
